/**
 * Map a typed `TourStep` to a react-joyride v3 `Step`, plus the engine-level
 * "tooltip always fully on-screen" guarantee.
 *
 * react-joyride v3 positions tooltips with Floating UI. When the anchored
 * target is TALLER or WIDER than the viewport (a whole page-root region),
 * no edge of it both sits inside the viewport and leaves room for the
 * tooltip — so a custom middleware detects the oversized reference and
 * centers the tooltip in the viewport instead (the same fallback joyride
 * uses for `placement: "center"`). For a normal-sized target the middleware
 * is a strict no-op and the standard offset/flip/shift chain applies.
 *
 * Pure (no React) so it is unit-testable.
 */
import type { FloatingOptions, Step } from 'react-joyride'
import type { Middleware, MiddlewareState } from '@floating-ui/react-dom'
import type { TourStep } from './types'

/** Minimum margin (px) between a clamped tooltip and the viewport edge. */
export const VIEWPORT_CLAMP_PADDING = 10

function viewportSize(): { width: number; height: number } {
  if (typeof window === 'undefined') return { width: 0, height: 0 }
  return { width: window.innerWidth || 0, height: window.innerHeight || 0 }
}

/**
 * True when the reference rect overflows the viewport on either axis such
 * that an edge-anchored tooltip cannot be guaranteed on-screen. A
 * non-positive viewport dimension (test/SSR) disables the check for that
 * axis rather than force-centering every tooltip.
 */
export function referenceOverflowsViewport(
  reference: { width: number; height: number },
  viewport: { width: number; height: number },
  padding = VIEWPORT_CLAMP_PADDING
): boolean {
  const tallerThanViewport = viewport.height > 0 && reference.height > viewport.height - padding * 2
  const widerThanViewport = viewport.width > 0 && reference.width > viewport.width - padding * 2
  return tallerThanViewport || widerThanViewport
}

/**
 * Floating UI middleware — the "tooltip always fully on-screen" guarantee.
 * Runs last in the chain so it has the final say. Two cases:
 *
 * 1. The spotlighted region is larger than the viewport (a whole page root):
 *    no edge-anchored position can work, so center the tooltip in the
 *    viewport (react-joyride's own `placement: "center"` fallback).
 * 2. The computed position leaves the tooltip partly off-screen (e.g. a wide
 *    page-root anchor where `auto` resolves to `left` with no room): clamp
 *    the coordinates back inside the viewport. A well-placed tooltip is
 *    untouched, so normal anchored steps keep their computed placement.
 */
export const viewportClampMiddleware: Middleware = {
  name: 'tourViewportClamp',
  fn(state: MiddlewareState) {
    const { x, y, rects } = state
    const viewport = viewportSize()
    if (viewport.width <= 0 || viewport.height <= 0) return {}
    if (referenceOverflowsViewport(rects.reference, viewport)) {
      // Never push past the top/left edge if the tooltip itself is taller/
      // wider than the viewport — keep the controls reachable.
      return {
        x: Math.max(VIEWPORT_CLAMP_PADDING, (viewport.width - rects.floating.width) / 2),
        y: Math.max(VIEWPORT_CLAMP_PADDING, (viewport.height - rects.floating.height) / 2),
      }
    }
    const clampedX = Math.max(
      VIEWPORT_CLAMP_PADDING,
      Math.min(x, viewport.width - rects.floating.width - VIEWPORT_CLAMP_PADDING)
    )
    const clampedY = Math.max(
      VIEWPORT_CLAMP_PADDING,
      Math.min(y, viewport.height - rects.floating.height - VIEWPORT_CLAMP_PADDING)
    )
    if (clampedX !== x || clampedY !== y) {
      return { x: clampedX, y: clampedY }
    }
    return {}
  },
}

/**
 * The engine-level floating config every step inherits. Also passed as the
 * props-level default on `<Joyride>` — the two compose via react-joyride's
 * deepMerge, so a future per-step override still keeps the viewport clamp.
 */
export const TOUR_FLOATING_OPTIONS: Partial<FloatingOptions> = {
  middleware: [viewportClampMiddleware],
}

/**
 * Build the react-joyride step. `forceCenter` converts an anchored step into
 * a viewport-centered one — used on mobile for sidebar nav anchors, where the
 * desktop sidebar element exists in the DOM but is hidden (`hidden md:flex`),
 * so spotlighting it would highlight nothing.
 */
export function toJoyrideStep(step: TourStep, forceCenter = false): Step {
  const isCenter = forceCenter || step.target === 'center'
  return {
    target: isCenter ? 'body' : `[data-tour="${step.target}"]`,
    title: step.title,
    content: step.body,
    placement: isCenter ? 'center' : (step.placement ?? 'auto'),
    // Page/section steps spotlight and go straight to the tooltip — no
    // floating beacon dot (v3 reads `skipBeacon`, not the v2 `disableBeacon`).
    skipBeacon: true,
    spotlightPadding: isCenter ? 0 : 8,
    floatingOptions: TOUR_FLOATING_OPTIONS,
  }
}
